<script setup lang="ts">
import { computed } from "vue";

/**
 * Rounded mono monogram for a subject — sidebar rows, the Add-subject list,
 * Today's comparison rows.
 *
 * Two letters, not one: at ~10 GCSEs a student can easily carry Biology,
 * Business and Chemistry at once, and a single initial makes those collide.
 */

const props = withDefaults(defineProps<{ name: string; size?: number }>(), {
  size: 44,
});

const initials = computed(() => {
  const words = props.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "??";
  // Two distinct words -> first letter of each ("English Literature" -> EL).
  // One word -> its first two letters ("Chemistry" -> CH).
  const raw =
    words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2);
  return raw.toUpperCase();
});
</script>

<template>
  <div
    class="monogram"
    :style="{
      width: `${size}px`,
      height: `${size}px`,
      fontSize: `${Math.round(size * 0.32)}px`,
    }"
    aria-hidden="true"
  >
    {{ initials }}
  </div>
</template>

<style scoped>
.monogram {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  font-family: var(--rl-font-mono);
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--rl-green-700);
  background: var(--rl-mint-100);
  border-radius: var(--radius-lg);
}
</style>
